import { Bell, Check, RefreshCcw, Search, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getEvents, setEventAcknowledgement, type EventPage } from '../api/events';
import { PageHeader } from '../components/PageHeader';
import { MobileFilterSelect } from '../components/MobileFilterSelect';
import { LoadingState, StatePanel } from '../components/StatePanel';
import { SeverityBadge } from '../components/StatusBadge';
import { useMonitoring } from '../context/MonitoringContext';
import type { EventSeverity } from '../types';
import { formatFullDateTime, formatCount } from '../utils/format';

const filters: { value: 'all' | EventSeverity; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
];

const dateBoundary = (value: string, end: boolean) => {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00`);
  if (!Number.isFinite(date.getTime())) return undefined;
  if (end) date.setDate(date.getDate() + 1);
  return new Date(date.getTime() - (end ? 1 : 0)).toISOString();
};

export function EventsPage() {
  const { devices } = useMonitoring();
  const [params, setParams] = useSearchParams();
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [data, setData] = useState<EventPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const severity = params.get('severity') || 'all';
  const deviceId = params.get('device') || '';
  const query = params.get('q') || '';
  const state = params.get('state') || 'all';
  const type = params.get('type') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  const updateParam = (key: string, value: string, defaultValue = 'all') => {
    const next = new URLSearchParams(params);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError('');
      void getEvents({
        severity: severity === 'all' ? undefined : severity,
        deviceId: deviceId || undefined,
        search: query || undefined,
        type: type || undefined,
        acknowledged: state === 'all' ? undefined : state === 'ack',
        from: dateBoundary(from, false),
        to: dateBoundary(to, true),
        page,
        pageSize: 50,
      }, controller.signal).then((result) => {
        if (!controller.signal.aborted) setData(result);
      }).catch((failure: unknown) => {
        if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : 'Не удалось загрузить события.');
      }).finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    }, 150);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [deviceId, from, page, query, revision, severity, state, to, type]);

  useEffect(() => {
    if (!autoUpdate) return;
    window.addEventListener('korsaapp:events-changed', refresh);
    refresh();
    return () => window.removeEventListener('korsaapp:events-changed', refresh);
  }, [autoUpdate, refresh]);

  const changeAcknowledgement = async (eventId: string, acknowledged: boolean) => {
    setPendingId(eventId);
    setError('');
    try {
      await setEventAcknowledgement(eventId, acknowledged);
      refresh();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Не удалось изменить состояние события.');
    } finally {
      setPendingId(null);
    }
  };

  if (loading && !data) return <LoadingState />;
  if (!data) return <StatePanel kind="error" title="События недоступны" description={error || 'Не удалось загрузить журнал событий.'} actionLabel="Повторить" onAction={refresh} />;
  const selectedDevice = devices.find((device) => device.id === deviceId);
  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <div className="page-stack events-page" aria-busy={loading}>
      <PageHeader
        eyebrow="Журнал мониторинга"
        title="События"
        description={`${formatCount(data.unacknowledgedCount, 'новое событие', 'новых события', 'новых событий')} требуют внимания`}
        actions={<div className="header-action-group">
          <label className="switch-control"><input type="checkbox" checked={autoUpdate} onChange={(event) => setAutoUpdate(event.target.checked)} /><span aria-hidden="true" /><em>Автообновление</em></label>
          <button className="button button--secondary" type="button" onClick={refresh}><RefreshCcw size={17} /> Обновить</button>
        </div>}
      />
      {error && <div className="form-error" role="alert">{error}</div>}
      {selectedDevice && <div className="active-filter-banner"><span>События устройства: <strong>{selectedDevice.name}</strong></span><button type="button" onClick={() => updateParam('device', '', '')} aria-label="Убрать фильтр устройства"><X size={16} /></button></div>}
      <section className="filter-panel events-filter-panel" aria-label="Фильтры событий">
        <div className="filter-panel__top">
          <label className="search-field"><Search size={18} aria-hidden="true" /><span className="sr-only">Поиск событий</span><input type="search" maxLength={200} value={query} onChange={(event) => updateParam('q', event.target.value, '')} placeholder="Устройство, тип или описание…" />{query && <button type="button" onClick={() => updateParam('q', '', '')} aria-label="Очистить поиск"><X size={16} /></button>}</label>
          <div className="segmented-control" role="group" aria-label="Состояние события">{[{ value: 'all', label: 'Все' }, { value: 'new', label: 'Новые' }, { value: 'ack', label: 'Подтверждённые' }].map((option) => <button key={option.value} type="button" className={state === option.value ? 'is-active' : ''} onClick={() => updateParam('state', option.value)}>{option.label}</button>)}</div>
        </div>
        <div className="mobile-filter-grid">
          <MobileFilterSelect label="Важность" value={severity} options={filters} onChange={(value) => updateParam('severity', value)} />
          <MobileFilterSelect label="Состояние" value={state} options={[{ value: 'all', label: 'Все' }, { value: 'new', label: 'Новые' }, { value: 'ack', label: 'Подтверждённые' }]} onChange={(value) => updateParam('state', value)} />
        </div>
        <div className="filter-scroll" role="group" aria-label="Важность события">{filters.map((filter) => <button key={filter.value} className={`filter-chip ${severity === filter.value ? 'is-active' : ''}`} type="button" onClick={() => updateParam('severity', filter.value)} aria-pressed={severity === filter.value}>{filter.label} <span>{filter.value === 'all' ? Object.values(data.severityCounts).reduce((sum, count) => sum + (count || 0), 0) : data.severityCounts[filter.value] || 0}</span></button>)}</div>
        <div className="filter-panel__top">
          <label className="search-field"><span>Тип</span><input aria-label="Тип события" maxLength={100} value={type} onChange={(event) => updateParam('type', event.target.value, '')} placeholder="DeviceStatusChanged" /></label>
          <label className="search-field"><span>С</span><input aria-label="События с даты" type="date" value={from} onChange={(event) => updateParam('from', event.target.value, '')} /></label>
          <label className="search-field"><span>По</span><input aria-label="События по дату" type="date" value={to} onChange={(event) => updateParam('to', event.target.value, '')} /></label>
        </div>
      </section>
      {data.items.length ? <section className="surface-card event-list-card" aria-label="Список событий">
        <div className="event-list-header desktop-only"><span>Важность</span><span>Событие</span><span>Дата и время</span><span>Состояние</span></div>
        <div className="event-list">{data.items.map((event) => <article className={`event-row ${!event.acknowledged ? 'is-new' : ''}`} key={event.id}>
          <SeverityBadge severity={event.severity} />
          <div className="event-row__content"><strong>{event.eventType}</strong>{event.deviceId ? <Link to={`/devices/${event.deviceId}`}>{event.deviceName || 'Удалённое устройство'}</Link> : <span>Системное событие</span>}<p>{event.description}</p></div>
          <time dateTime={event.timestamp}>{formatFullDateTime(event.timestamp)}</time>
          <button className="button button--quiet" type="button" disabled={pendingId !== null} onClick={() => void changeAcknowledgement(event.id, !event.acknowledged)} title={event.acknowledgedAtUtc ? `Подтверждено ${formatFullDateTime(event.acknowledgedAtUtc)}` : undefined}><Check size={15} />{pendingId === event.id ? 'Сохранение…' : event.acknowledged ? 'Отменить подтверждение' : 'Подтвердить'}</button>
        </article>)}</div>
      </section> : <StatePanel kind="empty" title="События не найдены" description="Для выбранных фильтров событий нет." actionLabel="Сбросить фильтры" onAction={() => setParams({}, { replace: true })} />}
      <nav className="header-action-group" aria-label="Страницы событий"><button className="button button--secondary" type="button" disabled={page <= 1 || loading} onClick={() => updateParam('page', String(page - 1), '1')}>Назад</button><span aria-live="polite">Страница {page} из {totalPages} · {formatCount(data.total, 'событие', 'события', 'событий')}</span><button className="button button--secondary" type="button" disabled={page >= totalPages || loading} onClick={() => updateParam('page', String(page + 1), '1')}>Далее</button></nav>
      <div className="events-footnote"><Bell size={15} aria-hidden="true" /><span>Новые события и подтверждения сохраняются на сервере и поступают через SignalR.</span></div>
    </div>
  );
}
